<template>
  <g
    class="BarChart"
    :class="[
      `BarChart--${isHorizontal ? 'horizontal' : 'vertical'}`,
      `BarChart--${alignValue}Align`,
    ]"
    :transform="`translate(${left}, ${top})`"
  >
    <g
      class="BarChart-content"
      :transform="`translate(${paddingX}, ${paddingY})`"
    >
      <template v-if="showBars">
        <g
          v-for="(bar, i) in bars"
          class="BarChart-bar"
          :transform="`translate(${bar[barXKey]}, ${bar[barYKey]})`"
          :key="i"
        >
          <template
            v-for="(child, j) in bar.children"
            :key="`bar-${i}-${j}-${child.fill}`"
          >
            <datalas-bar
              v-if="shadowOpacity && j == 0"
              class="Bar--bg"
              :style="{ opacity: shadowOpacity }"
              :key="`bg-${i}-${j}-${child.fill}`"
              :x="isHorizontal ? 0 - marginLeft - paddingX : child.bandPos"
              :y="isHorizontal ? child.bandPos : 0 - marginTop - paddingY"
              :width="
                isHorizontal
                  ? outerWidth + marginLeft + marginRight
                  : child.bandwidth
              "
              :height="
                isHorizontal
                  ? child.bandwidth
                  : outerHeight + marginTop + marginBottom
              "
              :is-negative="child.isNegative"
            />

            <datalas-bar
              :x="child[barXKey]"
              :y="child[barYKey]"
              :width="child[barWidthKey]"
              :height="child[barHeightKey]"
              :datum="child.datum"
              :fill="child.fill"
              :is-negative="child.isNegative"
              @datum:mouseover="$emit('datum:mouseover', $event)"
              @datum:mouseout="$emit('datum:mouseout', $event)"
              @datum:click="$emit('datum:click', $event)"
            />
          </template>
        </g>
      </template>

      <datalas-labels
        v-if="showLabels"
        :labels="labels"
        :prefix="labelPrefix"
        :suffix="labelSuffix"
        :format="labelFormat"
        :font-size="labelFontSize"
        :detect-collisions="noLabelCollisions"
        :filters="labelFilters"
        :offset-x="labelOffsetX"
        :offset-y="labelOffsetY"
        :style="{ pointerEvents: labelInside ? 'none' : undefined }"
      />
    </g>

    <teleport v-if="gridMounted" :to="`#${gridKey}`">
      <datalas-grid v-bind="gridLayout" />
    </teleport>
  </g>
</template>

<script>
import { isFinite } from 'lodash';

import Layer from './layer.vue';

export default {
  name: 'DatalasBars',

  extends: Layer,

  props: {
    align: {
      type: String,
    },
    normalize: {
      type: Boolean,
      default: false,
    },
    showBars: {
      type: Boolean,
      default: true,
    },
    shadowOpacity: {
      type: Number,
      default: 0,
    },
    dimensions: {
      type: Array,
      required: true,
      validator: (val) => {
        const length = val.find((d) => d.dimension === 'length');
        const fill = val.find((d) => d.dimension === 'fill');
        const bands = val.filter((d) => d.dimension === 'band');

        // TODO: don't allow normalize + nice on corresponding length scale
        return length && fill && bands.length;
      },
    },
  },

  emits: ['datum:mouseover', 'datum:mouseout', 'datum:click'],

  data: () => ({
    xDimension: 'band',
    yDimension: 'length',
  }),

  computed: {
    length() {
      return this.dimensions.find((d) => d.dimension === 'length').transform;
    },
    fill() {
      return this.dimensions.find((d) => d.dimension === 'fill').transform;
    },
    bands() {
      return this.dimensions
        .filter((d) => d.dimension === 'band')
        .map((d) => d.transform);
    },
    isAlignedToFarEdge() {
      return ['bottom', 'right'].includes(this.alignValue);
    },
    labels() {
      return this.bars.reduce(
        (acc, bar) => [
          ...acc,
          ...bar.children.map((child) => ({
            // 1. Get X position of the outer bar (which may contain a stack)
            x:
              bar[this.barXKey] +
              // 2. Add the x-pos of the bar in the stack
              (child[this.barXKey] || 0) +
              // 3. Offset the label based on the size of the bar
              this.getLabelX(child[this.barWidthKey], child.isNegative),

            // Finding Y position is same process as x above, but flipped
            y:
              bar[this.barYKey] +
              (child[this.barYKey] || 0) +
              this.getLabelY(child[this.barHeightKey], child.isNegative),

            value: child.datum[this.lengthTransform.field],

            // On negative values for horizontal charts, always set
            // labelAnchor to 'end'
            textAnchor:
              !this.labelInside && child.isNegative && this.isHorizontal
                ? 'end'
                : this.labelAnchorVal,

            // Same for negative vertical charts, but with dominantBaseline:
            // 'hanging'
            dominantBaseline:
              !this.labelInside && child.isNegative && !this.isHorizontal
                ? 'hanging'
                : this.labelBaselineVal,

            datum: child.datum,
          })),
        ],
        [],
      );
    },
    barXKey() {
      return this.isHorizontal ? 'lengthPos' : 'bandPos';
    },
    barYKey() {
      return this.isHorizontal ? 'bandPos' : 'lengthPos';
    },
    barWidthKey() {
      return this.isHorizontal ? 'length' : 'bandwidth';
    },
    barHeightKey() {
      return this.isHorizontal ? 'bandwidth' : 'length';
    },
    totalBandwidth() {
      return this.isHorizontal ? this.height : this.width;
    },
    totalLength() {
      return this.isHorizontal ? this.width : this.height;
    },
    lengthTransform() {
      return this.transforms[this.length];
    },
    bandTransforms() {
      return this.bands.map((b) => this.transforms[b]);
    },
    fillTransform() {
      return this.transforms[this.fill];
    },
    fillScale() {
      const { field } = this.fillTransform;

      if (this.fillTransform.options.type === 'scheme') {
        const domain = Object.keys(
          this.data.reduce((acc, datum) => {
            if (datum[field]) acc[datum[field]] = null;
            return acc;
          }, {}),
        );

        return this.fillTransform.transformer(domain);
      }

      return this.fillTransform.transformer();
    },
    nestedData() {
      const tree = this.$d3.nest().rollup((items) => items);

      this.bandTransforms.forEach((b) => {
        tree.key((d) => d[b.field]);
      });

      return tree.entries(this.data);
    },

    layoutTotalBandwidth() {
      if (this.isMobileWidth) {
        // step = band with + outer padding
        const chartStep = 24 + 25.9;
        const projectedTotalBandwidth = this.nestedData.length * chartStep;
        const totalBandwith =
          projectedTotalBandwidth > this.totalBandwidth
            ? projectedTotalBandwidth
            : this.totalBandwidth;

        return totalBandwith;
      }

      return this.totalBandwidth;
    },

    layoutTree() {
      return this.getLayoutTree(this.nestedData, this.layoutTotalBandwidth);
    },
    bars() {
      const lastIndex = this.bandTransforms.length - 1;
      return this.getFieldLayout(this.bandTransforms[lastIndex].field);
    },

    gridLayout() {
      const grid = { ...this.grid };
      grid.axes = grid.axes.map((axis) => {
        const props = { ...axis.props };
        const { scale } = props;

        if (scale === this.length) {
          props.scale = this.lengthScale;
        } else {
          const { field } = this.transforms[scale];
          const layout = this.getFieldLayout(field);
          props.scale = this.makeOrdinalScale(layout);
          props.tickFormat = this.makeOrdinalFormatter(layout, axis);

          if (!props.hideDomainPath) {
            props.domainOffset = props.scale.copy()();
          }
        }
        props.height = this.height;
        props.width = this.width;
        return { ...axis, props };
      });

      return grid;
    },
    min() {
      const { min } = this.lengthTransform.options;

      return min === undefined || min === null
        ? Math.min(0, this.$d3.min(this.data, this.lengthAccessor))
        : min;
    },
    max() {
      const { max } = this.lengthTransform.options;

      if (max === undefined || max === null) {
        const getVals = (tree) =>
          tree.reduce(
            (acc, { values, value }) =>
              acc.concat(values ? getVals(values) : [value]),
            [],
          );
        const bars = getVals(this.nestedData);

        return this.$d3.max(bars, (bar) => this.getLengthSum(bar));
      }

      return max;
    },
    range() {
      const range = [0];
      range[this.isAlignedToFarEdge ? 'unshift' : 'push'](this.totalLength);

      return range;
    },
    lengthScale() {
      return this.lengthTransform.transformer([this.min, this.max], this.range);
    },
  },

  methods: {
    getLabelX(width, isNegative) {
      let x = {
        top: width / 2,
        right: this.labelInside ? width / 2 : 0,
        bottom: width / 2,
        left: this.labelInside ? width / 2 : width,
      }[this.alignValue];

      if (isNegative && this.isHorizontal && !this.labelInside) {
        x = -((this.labelOffsetX || 0) * 2);
      }

      return x;
    },

    getLabelY(height, isNegative) {
      const h = isNegative ? -height : 0;

      let y = {
        top: this.labelInside ? height / 2 : height,
        right: height / 2,
        bottom: this.labelInside ? height / 2 : h,
        left: height / 2,
      }[this.alignValue];

      if (isNegative && !this.isHorizontal && !this.labelInside) {
        y = -y - (this.labelOffsetY || 0) * 2;
      }

      return y;
    },

    lengthAccessor(item) {
      return item[this.lengthTransform.field];
    },
    getLengthSum(items) {
      return this.$d3.sum(items, this.lengthAccessor);
    },
    makeOrdinalScale(layout) {
      return this.$d3
        .scaleOrdinal()
        .range(layout.map((l) => l.bandPos + l.bandwidth / 2))
        .domain(Object.keys(layout));
    },
    makeOrdinalFormatter(layout, axis) {
      const { field, label, tickFormat, tickFormatString } = axis;

      return (k) => {
        if (k === null) return '';

        const val = label
          ? this.getFieldValue(field, layout[k].value, label) || ''
          : layout[k].value;
        const formatter = tickFormatString || '';
        const format = tickFormat || this.$format(formatter);
        const formatted = isFinite(val) ? format(val) : val;

        const prefix = axis.tickPrefix || '';
        const suffix = axis.tickSuffix || '';

        return `${prefix}${formatted}${suffix}`;
      };
    },
    getLayoutTree(data, bandwidth, level = 0) {
      const scale = this.bandTransforms[level].transformer(
        data.map((d) => d.key),
        [0, bandwidth],
      );

      return data.map((d) => this.getGroupLayout(d, scale, level));
    },
    getGroupLayout(item, bandScale, level) {
      const bandwidth = bandScale.bandwidth();
      const group = {
        bandPos: bandScale(item.key),
        lengthPos: 0,
        field: this.bandTransforms[level].field,
        bandwidth,
      };

      group.children = item.value
        ? this.getBarLayout(item.value, bandwidth)
        : this.getLayoutTree(item.values, bandwidth, level + 1);

      return group;
    },
    getBarLayout(bar, bandwidth) {
      const items = bar.slice();
      const invert = this.isAlignedToFarEdge;
      const { field } = this.lengthTransform;

      const lengthScale = this.normalize
        ? this.lengthTransform.transformer(
            [this.min, this.getLengthSum(items)],
            this.range,
          )
        : this.lengthScale;
      const zero = lengthScale(0);

      // Note: this keeps the order of the items the same whether or not it's
      // flipped (align: left|right). Not sure if that's desired.
      // if (invert) items.reverse();

      // Stack items in bar
      return items.reduce((acc, datum, i) => {
        const val = datum[field];
        const isNegative = val < 0;
        const scaled = lengthScale(Math.abs(val));

        const edge = invert ? this.totalLength : 0;
        const baseline = Math[invert ? 'min' : 'max'](zero, edge);
        const length = invert ? baseline - scaled : scaled - baseline;

        const prev = acc[i - 1];
        const lengthStart = prev ? prev.lengthPos : baseline;
        const prevLength = prev ? prev.length : 0;

        const pos = isNegative ? zero - length : lengthStart + prevLength;
        const inversePos = isNegative ? zero : lengthStart - length;
        const lengthPos = invert ? inversePos : pos;

        const fillKey = datum[this.fillTransform.field];
        const fill = this.fillScale(fillKey);

        return acc.concat([
          { lengthPos, bandwidth, length, fill, datum, isNegative },
        ]);
      }, []);
    },
    getFieldLayout(field) {
      const getVal = (o) =>
        o.children ? getVal(o.children[0]) : o.datum[field];
      const getFieldLayout = ({ items, bandPos }, item) => {
        if (item.field === field) {
          items.push({
            bandPos: bandPos + item.bandPos,
            bandwidth: item.bandwidth,
            lengthPos: item.lengthPos,
            children: item.children,
            value: getVal(item.children[0]),
          });
        } else if (item.children) {
          item.children.forEach((child) => {
            getFieldLayout({ items, bandPos: bandPos + item.bandPos }, child);
          });
        }

        return { items, bandPos };
      };

      return this.layoutTree.reduce(getFieldLayout, { items: [], bandPos: 0 })
        .items;
    },
  },
};
</script>
